import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

export default function Footer() {
  const location = useLocation();
  const { t } = useTranslation();

  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Si on est déjà sur la page d'accueil, scroll vers la section
    if (location.pathname === '/') {
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Sinon, naviguer vers la page d'accueil avec l'ancre
      window.location.href = '/#features';
    }
  };

  return (
    <footer className="relative bg-black/50 border-t border-white/10 pt-20 pb-12">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,70,229,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(219,39,119,0.05),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo et coordonnées */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8 pb-8 border-b border-white/10">
          <div>
            <div className="mb-6">
              <img 
                src="https://i.imgur.com/3FXFggm.png" 
                alt="Botmaker Agency"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-8 max-w-md">
              {t('pages.home.hero.description')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.product.title')}</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="/#features" 
                    onClick={handleFeaturesClick}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('footer.product.features')}
                  </a>
                </li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">{t('footer.product.pricing')}</Link></li>
                <li>
                  <a 
                    href="https://botmaker-agency.ai/widget?id=953e8d21-dcfa-4050-bd3d-f4eca25f7064"
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('footer.product.guide')}
                  </a>
                </li>
                <li><Link to="/mini-apps" className="text-gray-400 hover:text-white transition-colors">{t('footer.product.miniApps')}</Link></li>
                <li><Link to="/integrations" className="text-gray-400 hover:text-white transition-colors">{t('footer.product.integrations')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.resources.title')}</h3>
              <ul className="space-y-3">
                <li><Link to="/documentation" className="text-gray-400 hover:text-white transition-colors">{t('footer.resources.documentation')}</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">{t('footer.resources.blog')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.company.title')}</h3>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">{t('footer.company.about')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">{t('footer.company.contact')}</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-white transition-colors">{t('footer.company.careers')}</Link></li>
                <li><Link to="/partners" className="text-gray-400 hover:text-white transition-colors">{t('footer.company.partners')}</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Liens légaux et copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center md:justify-start gap-x-8 gap-y-2 text-sm">
            <Link to="/legal" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.legal.mentions')}
            </Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.legal.privacy')}
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.legal.terms')}
            </Link>
            <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">
              {t('footer.legal.cookies')}
            </Link>
          </div>
          <div className="text-gray-400 text-sm">
            {t('footer.copyright')}
          </div>
        </div>
      </div>
    </footer>
  );
}